import { useMemo, useState } from "react";
import { ArrowUpRight, Download, Search, X } from "lucide-react";
import type { ResourceLoungeCard, ResourceLoungeContentProps } from "@/types/voice/genie-details";
import { formatSize } from "./geniePanelShared";
import { isSafeUrl } from "./infoLinkIcons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export const ResourceLoungeContent = ({ resources, helpHub, helpPageUrl, surfacedResourceId, brandColor, secondaryColor }: ResourceLoungeContentProps) => {
  const [query, setQuery] = useState("");
  const cards = useMemo(() => {
    const result: ResourceLoungeCard[] = [];
    if (helpHub) {
      for (const topic of helpHub.topics) for (const guide of topic.guides) {
        result.push({ id: `guide-${guide.id}`, sourceId: guide.id, kind: "Guide", title: guide.name,
          description: guide.summary || guide.answers[0] || topic.name,
          url: `/help/${encodeURIComponent(helpHub.urlName)}/${encodeURIComponent(guide.id)}`,
          genieIcon: "book-open", action: "Read guide" });
      }
    }
    const seen = new Set<string>();
    for (const doc of [...(resources?.documents ?? []), ...(helpHub?.downloads ?? [])]) {
      if (!doc.fileUrl || !isSafeUrl(doc.fileUrl) || seen.has(doc.fileUrl)) continue;
      seen.add(doc.fileUrl);
      const isPdf = doc.mimeType === "application/pdf" || /\.pdf$/i.test(doc.fileName ?? "");
      result.push({ id: `document-${doc.id}`, sourceId: doc.id, kind: "Download", title: doc.label || doc.fileName || "Document",
        description: [isPdf ? "PDF document" : "Downloadable file", formatSize(doc.sizeBytes ?? undefined)].filter(Boolean).join(" · "),
        url: doc.fileUrl, genieIcon: "files", action: isPdf ? "Download PDF" : "Download file", fileName: doc.fileName ?? undefined,
        imageUrl: /^image\/(png|jpeg|webp|gif)$/.test(doc.mimeType ?? "") ? doc.fileUrl : undefined });
    }
    for (const link of [...(resources?.links ?? []), ...(helpHub?.links ?? [])]) {
      if (!isSafeUrl(link.url) || seen.has(link.url)) continue;
      seen.add(link.url);
      result.push({ id: `link-${link.id}`, sourceId: link.id, kind: "Link", title: link.label || "Website",
        description: link.url.startsWith("/") ? "Website resource" : new URL(link.url).hostname.replace(/^www\./, ""),
        url: link.url, genieIcon: "globe", action: "Visit website" });
    }
    return result.sort((a, b) => Number(b.sourceId === surfacedResourceId) - Number(a.sourceId === surfacedResourceId));
  }, [resources, helpHub, surfacedResourceId]);

  const visible = cards.filter(card =>
    `${card.title} ${card.description}`.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <section className="min-w-0" aria-label="Resources">
      {(cards.length >= 12 || query) && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-genie-border bg-white px-4 focus-within:ring-2 focus-within:ring-genie-primary">
          <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-label="Search resources"
            placeholder="Find a resource"
            className="min-w-0 flex-1 bg-transparent py-3 text-base outline-none sm:text-sm"
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="grid min-h-[44px] min-w-[44px] place-items-center">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="overflow-hidden rounded-2xl border border-genie-border bg-white divide-y divide-genie-border">
        {visible.map(card => (
          <a
            key={card.id}
            href={card.url}
            target="_blank"
            rel="noopener noreferrer"
            download={card.fileName || undefined}
            aria-label={`${card.action}: ${card.title}`}
            className="group flex min-h-[88px] items-center gap-4 p-5 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-genie-primary sm:p-6"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl">
              {card.imageUrl ? (
                <img src={card.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" onError={event => { event.currentTarget.style.display = "none"; }} />
              ) : (
                <GenieIcon name={card.genieIcon} size={32} color={brandColor} secondaryColor={secondaryColor} gradientEnabled />
              )}
            </span>
            <span className="min-w-0 flex-1">
              {card.sourceId === surfacedResourceId && <span className="mb-1 block text-xs font-medium text-gray-500">From your conversation</span>}
              <span className="block break-words text-base font-semibold text-gray-900">{card.title}</span>
              <span className="mt-1 block line-clamp-2 break-words text-sm leading-relaxed text-gray-500">{card.description}</span>
            </span>
            {card.kind === "Download" ? <Download className="h-4 w-4 shrink-0 text-gray-400" /> : <ArrowUpRight className="h-4 w-4 shrink-0 text-gray-400" />}
          </a>
        ))}
        {helpPageUrl && !query && (
          <a href={helpPageUrl} target="_blank" rel="noopener noreferrer" className="flex min-h-[88px] items-center gap-4 p-5 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-genie-primary sm:p-6">
            <span className="grid h-11 w-11 shrink-0 place-items-center">
              <GenieIcon name="book-open" size={32} color={brandColor} secondaryColor={secondaryColor} gradientEnabled />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold text-gray-900">Help centre</span>
              <span className="mt-1 block text-sm text-gray-500">{cards.length ? "Browse the full support page." : "Visit the business’s support page."}</span>
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-gray-400" />
          </a>
        )}
        {visible.length === 0 && (query || !helpPageUrl) && (
          <p className="p-6 text-sm text-gray-500" role="status">{query ? "No resources match your search." : "No links or resources have been added yet."}</p>
        )}
      </div>
    </section>
  );
};
